import { useCallback } from "react"
import {
  NavigateOptions,
  createSearchParams,
  useNavigate
} from "react-router-dom"

import { merge } from "lodash-es"

import { TEAM_DETAILS } from "shared/constants/permalinks"
import { useEvent } from "shared/lib/useEvent"
import { useLocale } from "shared/lib/useLocale"

interface UseNavigateToTeamReturn<T extends string | null> {
  navigate: T extends string
    ? () => void
    : (payload?: {
        id: string
        params?: Record<string, string> | null
        options?: NavigateOptions | undefined
      }) => void
  getHref: T extends string ? () => string : (id: string) => string
}

export function useTeamLink<T extends string | null>(
  id: T,
  options?: NavigateOptions | undefined
): UseNavigateToTeamReturn<T>
export function useTeamLink<T extends string | null>(
  id: T,
  options?: NavigateOptions | undefined
) {
  const navigate = useNavigate()
  const { currentLocale } = useLocale()

  const getHref = useCallback(
    (teamID: string) => {
      return `/${currentLocale}/${TEAM_DETAILS}?team_id=${encodeURIComponent(
        teamID
      )}`
    },
    [currentLocale]
  )

  const getHrefWithoutID = useCallback(() => {
    return id ? getHref(id) : "#"
  }, [getHref])

  const navigateToTeam = useEvent(
    (payload?: {
      id: string
      params?: Record<string, string> | null
      options?: NavigateOptions | undefined
    }) => {
      const resolvedID = id || (payload?.id ?? "")
      const pathname = `/${currentLocale}/${TEAM_DETAILS}`

      navigate(
        {
          pathname,
          search: `?${createSearchParams({
            ...payload?.params,
            team_id: resolvedID ?? ""
          })}`
        },
        merge({}, options, payload?.options)
      )
    }
  )

  return {
    navigate: navigateToTeam,
    getHref: id ? getHrefWithoutID : getHref
  }
}
